import {
  useDataProvider,
  useNavigation,
  useNotification,
  useUpdate,
} from "@refinedev/core";

import { notifyMutationError } from "@/lib/repository-utils";
import { ScreeningEditFormType } from "@/pages/screenings/schemas/screening.edit.form";

/** `UpdateScreeningCommand` members that map one-to-one from the form. */
const PASSTHROUGH_KEYS = [
  "startDate",
  "format",
  "screen",
  "pricing",
  "status",
  "tags",
  "hide",
  "ticketUrl",
] as const;

export const useScreeningRepository = () => {
  const { getOne } = useDataProvider()();
  const { mutateAsync: updateAsync } = useUpdate({ resource: "screenings" });
  const { edit } = useNavigation();
  const { open } = useNotification();

  async function get(id: string): Promise<unknown> {
    return getOne({ id, resource: "screenings" });
  }

  /**
   * Partial update onto `UpdateScreeningCommand`. The denormalized `movie`/`venue` copies never
   * go out: a changed one is sent as its id (`movieId`/`venueId`) and the backend re-copies the
   * document — the command's own `venue` member, a caller-built copy, is not used.
   * `startDate` must already be the absolute instant (see `ScreeningsEdit`).
   */
  async function update(
    values: Partial<ScreeningEditFormType>,
    label?: string,
  ) {
    const payload: Record<string, unknown> = { id: values.id };
    for (const key of PASSTHROUGH_KEYS) {
      if (values[key] !== undefined) payload[key] = values[key];
    }
    if (values.movie?.id) payload.movieId = values.movie.id;
    if (values.venue?.id) payload.venueId = values.venue.id;

    const response = await updateAsync?.(
      { id: values.id!, values: payload },
      {
        onSuccess: () => {
          edit("screenings", values.id!, "replace");
          open?.({
            type: "success",
            message: "Successfully updated screening",
            description: `You updated ${label ?? "a screening"}`,
          });
        },
        onError: (error) => {
          console.error(
            "Failed to update screening with values",
            values,
            error,
          );
          notifyMutationError(open, "Update screening request", error);
        },
      },
    );

    return response?.data.id?.toString();
  }

  return { get, update };
};
